import React, { useEffect, useRef } from 'react';
import MoleculeDivBtn from '../../shared/component/molecule/MoleculeDivBtn';
import {
  DeviceType,
  MemberType,
  ModalType,
} from '../../shared/enum/EnumRepository';
import { useLinkedGroupMember } from '../../shared/hooks/useApi/useLinkedGroupMember';
import { useApprovalRequest } from '../../shared/hooks/useApi/useApprovalRequest';
import { useAuth } from '../../shared/hooks/useApi/useAuth';
import { useSetting } from '../../shared/hooks/useApi/useSetting';
import { useDeviceStore } from '../../shared/store/deviceStore';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { getSettingsMessage } from '../../shared/constants/pageMessages';
import {
  SettingsSection,
  SubContainer,
  SubExplain,
  SubTitle,
  SubValue,
  SubValueChange,
  SubValueChangeMobile,
  SubWrapper,
  Title,
} from './MemberSetting';

const ConnectionManagement: React.FC = () => {
  const { user } = useAuth();
  const { setting } = useSetting();
  const { deviceType } = useDeviceStore();
  const {
    connections,
    status,
    getConnections,
  } = useLinkedGroupMember();
  const getConnectionsRef = useRef(getConnections);
  getConnectionsRef.current = getConnections;
  const {
    approvalRequestList,
    status: approvalRequestStatus,
    getApprovalRequestList,
  } = useApprovalRequest();
  const getApprovalRequestListRef = useRef(getApprovalRequestList);
  getApprovalRequestListRef.current = getApprovalRequestList;
  const { setStandardModal } = useModalStore();
  const isIndividual = setting?.memberType === MemberType.INDIVIDUAL;

  useEffect(() => {
    if (!user?.aui) return;
    void getApprovalRequestListRef.current();
    if (isIndividual) void getConnectionsRef.current();
  }, [isIndividual, user?.aui]);

  if (!user || !setting) return null;

  const handleOpenApprovalRequests = () => {
    void getApprovalRequestListRef.current();
    setStandardModal({
      modalType: ModalType.APPROVAL_REQUEST_LIST,
      title: null,
      value: null,
      handleChange: () => {},
    });
  };

  const approvalRequestSummary = approvalRequestStatus === 'loading'
    || approvalRequestStatus === 'idle'
    ? getSettingsMessage('APPROVAL_REQUEST_LOADING')
    : approvalRequestStatus === 'error'
      ? getSettingsMessage('APPROVAL_REQUEST_FAILED')
      : approvalRequestList.length > 0
        ? getSettingsMessage('APPROVAL_REQUEST_COUNT', {
          count: approvalRequestList.length,
        })
        : getSettingsMessage('APPROVAL_REQUEST_NONE');

  const handleOpenConnections = () => {
    void getConnectionsRef.current();
    setStandardModal({
      modalType: ModalType.LINKED_GROUP_MEMBER_LIST,
      title: null,
      value: null,
      handleChange: () => {},
    });
  };

  const connectionSummary = status === 'loading' || status === 'idle'
    ? getSettingsMessage('CONNECTION_LOADING')
    : status === 'error'
      ? getSettingsMessage('CONNECTION_FAILED')
      : connections.length > 0
        ? getSettingsMessage('CONNECTION_COUNT', { count: connections.length })
        : getSettingsMessage('CONNECTION_NONE');

  return (
    <SettingsSection>
      <Title>Connection</Title>
      <SubContainer>
        <SubWrapper>
          <SubTitle>{getSettingsMessage('SETTINGS_APPROVAL_REQUESTS')}
            <SubExplain>
              {getSettingsMessage('APPROVAL_REQUESTS_EXPLAIN')}
            </SubExplain>
          </SubTitle>
          <MoleculeDivBtn
            value={approvalRequestSummary}
            defaultValue={approvalRequestSummary}
            handleClick={handleOpenApprovalRequests}
            DivChangeStyle={deviceType === DeviceType.MOBILE
              ? SubValueChangeMobile
              : SubValueChange}
            DivStyle={SubValue}
          />
        </SubWrapper>
        {isIndividual && (
          <SubWrapper>
            <SubTitle>
              {getSettingsMessage('SETTINGS_CONNECTIONS')}
              <SubExplain>{getSettingsMessage('CONNECTIONS_EXPLAIN')}</SubExplain>
            </SubTitle>
            <MoleculeDivBtn
              value={connectionSummary}
              defaultValue={connectionSummary}
              handleClick={handleOpenConnections}
              DivChangeStyle={deviceType === DeviceType.MOBILE
                ? SubValueChangeMobile
                : SubValueChange}
              DivStyle={SubValue}
            />
          </SubWrapper>
        )}
      </SubContainer>
    </SettingsSection>
  );
};

export default ConnectionManagement;
